/**
 * useProviderExams Hook
 * Custom hook for managing provider exam linking state
 */

import { useState, useCallback } from 'react';
import { providerExamService } from '../../../services/providerExamService';
import {
  ProviderLinkedExam,
  ProviderAvailableExam,
  ProviderExamBulkItem,
  ProviderExamBulkResult,
} from '../../../types/providerExam';

interface UseProviderExamsState {
  linked: ProviderLinkedExam[];
  available: ProviderAvailableExam[];
  loading: boolean;
  error: string | null;
}

interface UseProviderExamsReturn extends UseProviderExamsState {
  loadExams: (providerId: number) => Promise<void>;
  bulkUpsert: (providerId: number, items: ProviderExamBulkItem[]) => Promise<ProviderExamBulkResult | null>;
  unlinkSingle: (providerId: number, examId: number) => Promise<boolean>;
  clearError: () => void;
}

export function useProviderExams(): UseProviderExamsReturn {
  const [state, setState] = useState<UseProviderExamsState>({
    linked: [],
    available: [],
    loading: false,
    error: null,
  });

  const loadExams = useCallback(async (providerId: number): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await providerExamService.getForProvider(providerId);
      setState((prev) => ({
        ...prev,
        linked: response.linked,
        available: response.available,
        loading: false,
      }));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message ?? error.message ?? 'Erro ao carregar exames',
      }));
    }
  }, []);

  const bulkUpsert = useCallback(
    async (providerId: number, items: ProviderExamBulkItem[]): Promise<ProviderExamBulkResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await providerExamService.bulkUpsert(providerId, items);
        const response = await providerExamService.getForProvider(providerId);
        setState((prev) => ({ ...prev, linked: response.linked, available: response.available, loading: false }));
        return result;
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } }; message?: string };
        setState((prev) => ({ ...prev, loading: false, error: error.response?.data?.message ?? error.message ?? 'Erro ao salvar exames' }));
        return null;
      }
    },
    []
  );

  const unlinkSingle = useCallback(
    async (providerId: number, examId: number): Promise<boolean> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        await providerExamService.unlinkExam(providerId, examId);
        setState((prev) => ({ ...prev, loading: false }));
        return true;
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } }; message?: string };
        setState((prev) => ({ ...prev, loading: false, error: error.response?.data?.message ?? error.message ?? 'Erro ao desvincular exame' }));
        return false;
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    loadExams,
    bulkUpsert,
    unlinkSingle,
    clearError,
  };
}

export default useProviderExams;
